/**
 * Tenants Redux Sagas
 */

import { call, put, takeLatest, select } from 'redux-saga/effects'
import { tenantsActions } from './tenants.slice'
import { tenantsApi } from '../infrastructure/api/tenants-api'
import { selectTenantsFilter } from './tenants.selectors'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { CustomerGroupOption, Tenant, TenantsFilter, TenantsListResponse } from '../domain/types/tenant.types'

function* fetchTenantsSaga(action: PayloadAction<TenantsFilter | undefined>) {
  try {
    // Get current filter from state and merge with action payload
    const currentFilter: TenantsFilter = yield select(selectTenantsFilter)
    const filter = action.payload ? { ...currentFilter, ...action.payload } : currentFilter

    const response: TenantsListResponse = yield call(tenantsApi.getList, filter)
    yield put(tenantsActions.fetchTenantsSuccess(response))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch tenants'
    yield put(tenantsActions.fetchTenantsFailure(message))
  }
}

function* approveTenantSaga(
  action: PayloadAction<{
    tenantId: string
    unblockUsers?: boolean
    customerGroupDocumentId?: string
    sapCustomerGroup?: string
  }>
) {
  try {
    const tenant: Tenant = yield call(
      tenantsApi.approve,
      action.payload.tenantId,
      action.payload.unblockUsers,
      action.payload.customerGroupDocumentId,
      action.payload.sapCustomerGroup
    )
    yield put(tenantsActions.approveTenantSuccess(tenant))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to approve tenant'
    yield put(tenantsActions.actionFailure(message))
  }
}

function* fetchCustomerGroupsSaga() {
  try {
    const customerGroups: CustomerGroupOption[] = yield call(tenantsApi.fetchCustomerGroups)
    yield put(tenantsActions.fetchCustomerGroupsSuccess(customerGroups))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch customer groups'
    yield put(tenantsActions.fetchCustomerGroupsFailure(message))
  }
}

function* suspendTenantSaga(action: PayloadAction<{ tenantId: string }>) {
  try {
    const tenant: Tenant = yield call(tenantsApi.suspend, action.payload.tenantId)
    yield put(tenantsActions.suspendTenantSuccess(tenant))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to suspend tenant'
    yield put(tenantsActions.actionFailure(message))
  }
}

function* updateTenantSaga(action: PayloadAction<{ tenantId: string; data: Partial<Tenant>; customerGroupId?: string }>) {
  try {
    const tenant: Tenant = yield call(
      tenantsApi.update,
      action.payload.tenantId,
      action.payload.data,
      action.payload.customerGroupId
    )
    yield put(tenantsActions.updateTenantSuccess(tenant))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update tenant'
    yield put(tenantsActions.actionFailure(message))
  }
}

function* createTenantSaga(action: PayloadAction<Omit<Tenant, 'id' | 'documentId' | 'createdAt' | 'updatedAt'>>) {
  try {
    const tenant: Tenant = yield call(tenantsApi.create, action.payload)
    yield put(tenantsActions.createTenantSuccess(tenant))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create tenant'
    yield put(tenantsActions.actionFailure(message))
  }
}

function* deleteTenantSaga(action: PayloadAction<{ tenantId: string }>) {
  try {
    yield call(tenantsApi.delete, action.payload.tenantId)
    yield put(tenantsActions.deleteTenantSuccess(action.payload.tenantId))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete tenant'
    yield put(tenantsActions.actionFailure(message))
  }
}

// Re-fetch tenants when filter or pagination changes
function* refetchOnFilterChangeSaga() {
  yield put(tenantsActions.fetchTenants(undefined))
}

// Re-fetch tenants after successful mutations to reflect changes
function* refetchAfterMutationSaga() {
  yield put(tenantsActions.fetchTenants(undefined))
}

export function* tenantsSaga() {
  yield takeLatest(tenantsActions.fetchTenants.type, fetchTenantsSaga)
  yield takeLatest(tenantsActions.approveTenant.type, approveTenantSaga)
  yield takeLatest(tenantsActions.fetchCustomerGroups.type, fetchCustomerGroupsSaga)
  yield takeLatest(tenantsActions.suspendTenant.type, suspendTenantSaga)
  yield takeLatest(tenantsActions.updateTenant.type, updateTenantSaga)
  yield takeLatest(tenantsActions.createTenant.type, createTenantSaga)
  yield takeLatest(tenantsActions.deleteTenant.type, deleteTenantSaga)

  // Re-fetch on filter/pagination changes
  yield takeLatest(tenantsActions.setFilter.type, refetchOnFilterChangeSaga)
  yield takeLatest(tenantsActions.setPage.type, refetchOnFilterChangeSaga)
  yield takeLatest(tenantsActions.setPageSize.type, refetchOnFilterChangeSaga)
  yield takeLatest(tenantsActions.filterByStatus.type, refetchOnFilterChangeSaga)
  yield takeLatest(tenantsActions.filterByChannel.type, refetchOnFilterChangeSaga)
  yield takeLatest(tenantsActions.searchTenants.type, refetchOnFilterChangeSaga)

  // Re-fetch after mutations to ensure list is current
  yield takeLatest(tenantsActions.createTenantSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(tenantsActions.updateTenantSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(tenantsActions.approveTenantSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(tenantsActions.suspendTenantSuccess.type, refetchAfterMutationSaga)
  yield takeLatest(tenantsActions.deleteTenantSuccess.type, refetchAfterMutationSaga)
}
